import _extends from "@babel/runtime/helpers/esm/extends";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

import React, { useRef } from 'react';
import styled from 'styled-components';
import { Text } from "../Text";
import { theme } from "../../theme";
import { useMousePosition } from "../../hooks/useMousePosition/useMousePosition";
import { MediaThumbnailAtFrame } from "./MediaThumbnailAtFrame";
import { getIconConfigForAsset } from "./getIconConfigForAsset";
import { getScaledDimensions } from "./getScaledDimensions";
import { Box } from "../Box";
/**
 * This is a 1px transparent image which displays as a fallback in order to
 * avoid default browser behavior for `img` tags with a missing source. Image
 * via https://stackoverflow.com/a/9967193/1137699.
 */

var _transparentPixelDataUriImage = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
var StyledImg = styled.img.withConfig({
  displayName: "MediaThumbnail__StyledImg",
  componentId: "vapor__sc-1ikbf3u-0"
})(["position:absolute;top:0;left:0;width:100%;height:100%;color:transparent;text-align:center;text-indent:10000px;object-fit:", ";pointer-events:none;"], function (p) {
  return p.$isImageFitPreferred ? 'contain' : 'cover';
});
var StyledMediaThumbnailAtFrame = styled(MediaThumbnailAtFrame).withConfig({
  displayName: "MediaThumbnail__StyledMediaThumbnailAtFrame",
  componentId: "vapor__sc-1ikbf3u-1"
})([""]);
var StyledSpan = styled.span.withConfig({
  displayName: "MediaThumbnail__StyledSpan",
  componentId: "vapor__sc-1ikbf3u-2"
})(["position:absolute;top:0;left:0;width:", "px;height:100%;background-color:", ";opacity:0;pointer-events:none;"], function (p) {
  return p.$width;
}, theme.colors.palette.alertError500);
var IconBackground = styled.div.withConfig({
  displayName: "MediaThumbnail__IconBackground",
  componentId: "vapor__sc-1ikbf3u-3"
})(["position:absolute;top:", ";left:", ";transform:", ";background:", ";width:", ";height:", ";border:", ";border-radius:", ";opacity:0.5;"], function (p) {
  return p.$isIconFallbackFullBleed ? 0 : '50%';
}, function (p) {
  return p.$isIconFallbackFullBleed ? 0 : '50%';
}, function (p) {
  return !p.$isIconFallbackFullBleed && "translate(-50%, -50%)";
}, function (p) {
  return p.$isIconFallbackBackgroundVisible && "radial-gradient(\n      at top left,\n      rgba(255,255,255,.15),\n      rgba(0,0,0,.15)\n    ),\n    ".concat(theme.colors.background.filetype[p.$bgColor]);
}, function (p) {
  return p.$width ? "".concat(p.$width, "px") : '100%';
}, function (p) {
  return p.$height ? "".concat(p.$height, "px") : '100%';
}, function (p) {
  return p.$isIconFallbackBackgroundVisible && "1px solid ".concat(theme.colors.background.filetype[p.$borderColor]);
}, function (p) {
  return typeof p.$borderRadius === 'string' ? p.$borderRadius : theme.radii[p.$borderRadius];
});
var StyledDiv = styled.div.withConfig({
  displayName: "MediaThumbnail__StyledDiv",
  componentId: "vapor__sc-1ikbf3u-4"
})(["position:relative;flex-shrink:0;display:flex;flex-direction:column;align-items:center;justify-content:center;width:100%;height:100%;background-color:", ";border-radius:", ";text-align:center;overflow:hidden;user-select:none;&:hover{", "{opacity:", ";}", ",", "{opacity:1;}}"], function (p) {
  return theme.colors.palette[p.$backgroundColor];
}, function (p) {
  return typeof p.$borderRadius === 'string' ? p.$borderRadius : theme.radii[p.$borderRadius];
}, StyledImg, function (p) {
  return p.$isScrubbable ? 0 : undefined;
}, StyledMediaThumbnailAtFrame, StyledSpan);
var StyledText = styled(Text).withConfig({
  displayName: "MediaThumbnail__StyledText",
  componentId: "vapor__sc-1ikbf3u-5"
})(["position:absolute;left:", ";right:", ";top:", ";bottom:", ";padding:", " ", ";background-color:", ";border-radius:", ";svg{margin-right:", ";width:", ";height:", ";transform:translateY(2px);}"], function (p) {
  return p.$left;
}, function (p) {
  return p.$right;
}, function (p) {
  return p.$top;
}, function (p) {
  return p.$bottom;
}, theme.spaces[2], theme.spaces[6], theme.colors.palette.grey12, theme.spaces[4], theme.spaces[2], theme.spaces[12], theme.spaces[12]); // HACK: not final, and using an interface so extra params are allowed
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions

/**
 * MediaThumbnail including hover scrubbing on supported assets. Scales to fit
 * provided container height and width. If provided, duration display and
 * secondary content appear over the thumbnail above a minimum width.
 *
 * @todo cover image vs thumb image vs VersionStackAsset
 * @todo uploading/processing state
 * @todo curtain on load
 */

export function MediaThumbnail(_ref) {
  var _media$thumbs, _media$thumbs2, _media$thumbs3, _ref$current$querySel, _ref$current, _media$metadata$origi, _media$metadata, _media$metadata$origi2, _media$metadata2;

  var assetTypename = _ref.assetTypename,
      _ref$backgroundColor = _ref.backgroundColor,
      backgroundColor = _ref$backgroundColor === void 0 ? 'grey10' : _ref$backgroundColor,
      _ref$borderRadius = _ref.borderRadius,
      borderRadius = _ref$borderRadius === void 0 ? 4 : _ref$borderRadius,
      children = _ref.children,
      contentBottomLeft = _ref.contentBottomLeft,
      contentBottomRight = _ref.contentBottomRight,
      contentTopLeft = _ref.contentTopLeft,
      contentTopRight = _ref.contentTopRight,
      height = _ref.height,
      isImageFitPreferred = _ref.isImageFitPreferred,
      _ref$isIconFallbackFu = _ref.isIconFallbackFullBleed,
      isIconFallbackFullBleed = _ref$isIconFallbackFu === void 0 ? true : _ref$isIconFallbackFu,
      _ref$isScrubEnabled = _ref.isScrubEnabled,
      isScrubEnabled = _ref$isScrubEnabled === void 0 ? true : _ref$isScrubEnabled,
      media = _ref.media,
      _ref$scrubBarWidth = _ref.scrubBarWidth,
      scrubBarWidth = _ref$scrubBarWidth === void 0 ? 2 : _ref$scrubBarWidth,
      style = _ref.style,
      _ref$isIconFallbackBa = _ref.isIconFallbackBackgroundVisible,
      isIconFallbackBackgroundVisible = _ref$isIconFallbackBa === void 0 ? true : _ref$isIconFallbackBa,
      _ref$isIconFallbackDi = _ref.isIconFallbackDisplayTextVisible,
      isIconFallbackDisplayTextVisible = _ref$isIconFallbackDi === void 0 ? true : _ref$isIconFallbackDi,
      width = _ref.width,
      rest = _objectWithoutProperties(_ref, ["assetTypename", "backgroundColor", "borderRadius", "children", "contentBottomLeft", "contentBottomRight", "contentTopLeft", "contentTopRight", "height", "isImageFitPreferred", "isIconFallbackFullBleed", "isScrubEnabled", "media", "scrubBarWidth", "style", "isIconFallbackBackgroundVisible", "isIconFallbackDisplayTextVisible", "width"]);

  var ref = useRef();
  var positionX = useMousePosition(ref);
  var isSmall = width < 100 || height < 100;

  var _getIconConfigForAsse = getIconConfigForAsset({
    assetTypename: assetTypename,
    mimeType: media.mimeType
  }),
      color = _getIconConfigForAsse.color,
      bgColor = _getIconConfigForAsse.backgroundColor,
      Icon = _getIconConfigForAsse.Icon,
      displayText = _getIconConfigForAsse.displayText;

  var scrubSpriteSrc = media === null || media === void 0 ? void 0 : (_media$thumbs = media.thumbs) === null || _media$thumbs === void 0 ? void 0 : _media$thumbs.thumbScrub;
  var thumbnailSrc = media === null || media === void 0 ? void 0 : (_media$thumbs2 = media.thumbs) === null || _media$thumbs2 === void 0 ? void 0 : _media$thumbs2.thumb;
  var thumbnail540Src = media === null || media === void 0 ? void 0 : (_media$thumbs3 = media.thumbs) === null || _media$thumbs3 === void 0 ? void 0 : _media$thumbs3.thumb540;
  var isScrubbable = Boolean(isScrubEnabled && scrubSpriteSrc); // HACK: should come from input

  var _ref2 = (_ref$current$querySel = (_ref$current = ref.current) === null || _ref$current === void 0 ? void 0 : _ref$current.querySelector('img')) !== null && _ref$current$querySel !== void 0 ? _ref$current$querySel : {},
      naturalWidth = _ref2.naturalWidth,
      naturalHeight = _ref2.naturalHeight;

  var _getScaledDimensions = getScaledDimensions(width, height, (_media$metadata$origi = media === null || media === void 0 ? void 0 : (_media$metadata = media.metadata) === null || _media$metadata === void 0 ? void 0 : _media$metadata.originalWidth) !== null && _media$metadata$origi !== void 0 ? _media$metadata$origi : naturalWidth, (_media$metadata$origi2 = media === null || media === void 0 ? void 0 : (_media$metadata2 = media.metadata) === null || _media$metadata2 === void 0 ? void 0 : _media$metadata2.originalHeight) !== null && _media$metadata$origi2 !== void 0 ? _media$metadata$origi2 : naturalHeight, isImageFitPreferred ? 'contain' : 'cover'),
      _getScaledDimensions2 = _slicedToArray(_getScaledDimensions, 2),
      scrubWidth = _getScaledDimensions2[0],
      scrubHeight = _getScaledDimensions2[1];

  return /*#__PURE__*/React.createElement(StyledDiv, _extends({
    ref: ref,
    $backgroundColor: backgroundColor,
    $borderRadius: borderRadius,
    $isScrubbable: isScrubbable,
    style: _objectSpread(_objectSpread({}, style), {}, {
      height: isIconFallbackFullBleed ? height : '100%',
      width: isIconFallbackFullBleed ? width : '100%'
    })
  }, rest), !thumbnailSrc && Icon && /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "".concat(width),
    height: "".concat(height)
  }, /*#__PURE__*/React.createElement(IconBackground, {
    $bgColor: bgColor,
    $borderColor: color,
    $borderRadius: borderRadius,
    $width: width,
    $height: height,
    $isIconFallbackFullBleed: isIconFallbackFullBleed,
    $isIconFallbackBackgroundVisible: isIconFallbackBackgroundVisible
  }), /*#__PURE__*/React.createElement(Box, {
    style: {
      zIndex: 1
    },
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column"
  }, /*#__PURE__*/React.createElement(Icon, {
    color: theme.colors.icon.filetype[color],
    height: isSmall ? 20 : 40,
    width: isSmall ? 20 : 40
  }), isIconFallbackDisplayTextVisible && /*#__PURE__*/React.createElement(Text, {
    color: "whiteA90",
    style: {
      fontSize: isSmall ? '12px' : '18px',
      fontWeight: 600,
      lineHeight: isSmall && '12px'
    }
  }, displayText))), /*#__PURE__*/React.createElement(StyledImg, {
    $isImageFitPreferred: isImageFitPreferred,
    alt: "",
    sizes: thumbnailSrc && thumbnail540Src ? '(max-width: 540px) 240px, 540px' : undefined,
    src: thumbnailSrc !== null && thumbnailSrc !== void 0 ? thumbnailSrc : _transparentPixelDataUriImage,
    srcSet: thumbnailSrc && thumbnail540Src ? "".concat(thumbnailSrc, " 240w, ").concat(thumbnail540Src, " 540w") : undefined
  }), isScrubbable && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(StyledMediaThumbnailAtFrame, {
    height: scrubHeight,
    position: positionX / width,
    src: scrubSpriteSrc,
    width: scrubWidth
  }), /*#__PURE__*/React.createElement(StyledSpan, {
    $width: scrubBarWidth,
    style: {
      transform: "translateX(".concat(positionX, "px)")
    }
  })), contentBottomRight && /*#__PURE__*/React.createElement(StyledText, {
    $bottom: theme.spaces[8],
    $right: theme.spaces[8],
    variant: "secondary"
  }, contentBottomRight), contentBottomLeft && /*#__PURE__*/React.createElement(StyledText, {
    $bottom: theme.spaces[8],
    $left: theme.spaces[8],
    variant: "secondary"
  }, contentBottomLeft), contentTopRight && /*#__PURE__*/React.createElement(StyledText, {
    $top: theme.spaces[8],
    $right: theme.spaces[8],
    variant: "secondary"
  }, contentTopRight), contentTopLeft && /*#__PURE__*/React.createElement(StyledText, {
    $top: theme.spaces[8],
    $left: theme.spaces[8],
    variant: "secondary"
  }, contentTopLeft), children);
}